import { useMemo, useState } from "react";
import { getCountries } from "react-phone-number-input";
import countryFlags from "react-phone-number-input/flags";
import labels from "react-phone-number-input/locale/en.json";

const FormDocumentInput = ({
  label = "CPF",
  name = "document",
  placeholder = "Informe seu documento",
  help = "",
  float = false,
  value = "",
  onChange,
  required = false,
  disabled = false,
  readOnly = false,
  country = "BR",
  onCountryChange,
  countryOptions,
  invalid = false,
  autoComplete = "off",
  inputClassName = "",
  containerClassName = "",
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const wrapperClassName = useMemo(() => {
    const base = float
      ? "form-floating PhoneInputFloating"
      : "form-group PhoneInputGroup";
    const hasUserValue =
      typeof value === "string" ? value.trim().length > 0 : !!value;
    const filled = hasUserValue ? " has-value" : "";
    const focused = isFocused ? " is-focused" : "";
    return `${base}${filled}${focused}`.trim();
  }, [float, value, isFocused]);

  const normalizedValue = value || "";

  const countryOptionsResolved = useMemo(() => {
    const sortByLabel = (options) =>
      [...options].sort((a, b) => {
        const byLabel = a.label.localeCompare(b.label, "en", {
          sensitivity: "base",
        });

        if (byLabel !== 0) {
          return byLabel;
        }

        return a.value.localeCompare(b.value);
      });

    if (Array.isArray(countryOptions) && countryOptions.length > 0) {
      return sortByLabel(
        countryOptions.map((countryItem) => ({
          value: countryItem.code,
          label: labels[countryItem.code] || countryItem.code,
        })),
      );
    }

    return sortByLabel(
      getCountries().map((countryCode) => ({
        value: countryCode,
        label: labels[countryCode] || countryCode,
      })),
    );
  }, [countryOptions]);

  const SelectedFlag = country ? countryFlags[country] : null;
  const selectedCountryLabel =
    countryOptionsResolved.find((option) => option.value === country)?.label ||
    labels.ZZ ||
    "International";

  const handleChange = (event) => {
    if (typeof onChange === "function") {
      onChange(event.target.value || "");
    }
  };

  const handleCountryChange = (event) => {
    if (typeof onCountryChange === "function") {
      onCountryChange(event.target.value);
    }
  };

  const showInvalid = invalid && !!normalizedValue;

  return (
    <div
      className={wrapperClassName}
      onFocusCapture={() => setIsFocused(true)}
      onBlurCapture={() => setIsFocused(false)}
    >
      {!float && label && (
        <label htmlFor={name} className="form-label">
          {label}
        </label>
      )}

      <div className={`PhoneInputBootstrap ${containerClassName}`.trim()}>
        <div className="PhoneInputCountry">
          <select
            className={`PhoneInputCountrySelect PhoneInputBootstrapCountry${showInvalid ? " is-invalid" : ""}`}
            value={country || ""}
            onChange={handleCountryChange}
            disabled={disabled || readOnly}
            aria-label={labels.country || "País"}
          >
            {countryOptionsResolved.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div className="PhoneInputCountryIcon PhoneInputCountryIcon--border">
            {SelectedFlag ? (
              <SelectedFlag
                title={selectedCountryLabel}
                className="PhoneInputCountryIconImg"
              />
            ) : null}
          </div>

          <div className="PhoneInputCountrySelectArrow" />
        </div>

        <input
          id={name}
          name={name}
          type="text"
          className={`form-control PhoneInputBootstrapInput${showInvalid ? " is-invalid" : ""} ${
            inputClassName || ""
          }`.trim()}
          placeholder={float ? " " : placeholder}
          value={normalizedValue}
          onChange={handleChange}
          required={required}
          disabled={disabled || readOnly}
          readOnly={readOnly}
          autoComplete={autoComplete}
        />
      </div>

      {float && label && <label htmlFor={name}>{label}</label>}

      {help && <small className="form-text text-muted">{help}</small>}
    </div>
  );
};

export default FormDocumentInput;
